import * as Sentry from "@sentry/react";

// Next's webpack dev build names our frames `webpack-internal:///…`. No `blob:`:
// our blob URLs are upload and image previews, never scripts.
const OUR_SCRIPT_URL_PARTS = ["/_next/static/", "webpack-internal://"];

const DROP_AUDIT_SAMPLE_RATE = 0.01;

const sentryEnvironment = () => {
  const env = process.env.NEXT_PUBLIC_DEPLOYMENT_ENVIRONMENT || "developer";
  switch (env) {
    case "prod":
      return "production";
    case "dev":
      return "development";
    default:
      return env;
  }
};

// Sentry's logs product is deliberately off — `consoleLoggingIntegration()`
// patches the global console, so every third-party script in the bundle bills its
// output to our logs quota.
Sentry.init({
  dsn: "https://7f22939566d18c7d52d22c8808a7f456@o4506152281374720.ingest.us.sentry.io/4506157510295552",
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined,
  environment: sentryEnvironment(),
  enabled: process.env.NEXT_PUBLIC_SENTRY_ENABLED === "1",

  beforeSend(event) {
    const urls = (event.exception?.values ?? [])
      .flatMap(value => value.stacktrace?.frames ?? [])
      .map(frame => frame.filename)
      .filter((filename): filename is string => !!filename?.includes("://"));
    const thirdPartyOnly =
      urls.length > 0 &&
      !urls.some(url => OUR_SCRIPT_URL_PARTS.some(part => url.includes(part)));
    if (!thirdPartyOnly) {
      return event;
    }
    if (Math.random() >= DROP_AUDIT_SAMPLE_RATE) {
      return null;
    }
    return { ...event, tags: { ...event.tags, filtered: "third_party_only" } };
  },

  // The throwing frame decides here, so an extension error called from our
  // code is still dropped. `iabjs://` is the Facebook Android in-app browser's
  // native bridge.
  denyUrls: [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,
    /^safari-web-extension:\/\//,
    /^iabjs:\/\//,
  ],

  // Third-party noise, not first-party bugs. `window.webkit.messageHandlers`:
  // iOS WKWebView in-app browsers probing for native bridges. `Blocked a frame
  // with origin`: Amplitude rrweb reading Stripe's cross-origin iframe. `Object
  // Not Found Matching Id`: Outlook Safe Links' headless browser scanning links
  // in our emails (getsentry/sentry-javascript#3440).
  ignoreErrors: [
    /window\.webkit\.messageHandlers/,
    /Blocked a frame with origin/,
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  ],

  // Strip query params — they can contain presigned tokens, API keys, etc.
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category !== "xhr" && breadcrumb.category !== "fetch") {
      return breadcrumb;
    }
    const data = breadcrumb.data;
    if (!data || typeof data.url !== "string") {
      return breadcrumb;
    }
    try {
      const url = new URL(data.url);
      data.url = `${url.origin}${url.pathname}`;
    } catch {
      // Invalid URL, keep as-is
    }
    return breadcrumb;
  },
});
